import { debug } from '../utils/debug';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadLanguage } from '../i18n/config';
import { captureWarning } from '../utils/sentry';

/**
 * Language Context
 *
 * Manages language state and provides translation utilities for dynamic content.
 * Works alongside i18next for fixed text translations.
 *
 * Fixed text (UI strings): Handled by i18next
 * Dynamic content (from database): Handled by this context
 *
 * IMPORTANT DESIGN PRINCIPLE: Avoid Data Redundancy
 * - Main tables contain DEFAULT language content
 * - Translation tables contain ONLY non-default languages
 * - Fallback chain: requested_lang (if != default) → default_lang (main table) → fallback
 */
const LanguageContext = createContext(null);

/**
 * Language Provider Component
 *
 * Provides language state and translation utilities for dynamic content.
 *
 * @param {object} merchantData - Merchant configuration with supported languages
 * @param {string} merchantData.default_language - Default language code (e.g., 'en')
 * @param {array} merchantData.supported_languages - Array of supported language codes
 */
export const LanguageProvider = ({ merchantData, children }) => {
  const { i18n } = useTranslation();

  // Get initial language with priority: URL > localStorage > browser > merchant default
  const getInitialLanguage = () => {
    const supportedLanguages = merchantData?.supported_languages || ['en'];

    // 1. Check URL parameter (highest priority - explicit user intent)
    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');

    if (queryStart !== -1) {
      const queryString = hash.substring(queryStart + 1);
      const urlParams = new URLSearchParams(queryString);
      const urlLang = urlParams.get('lang');

      if (urlLang && supportedLanguages.includes(urlLang)) {
        return urlLang;
      }
    }

    // 2. Check localStorage (user's previous selection)
    // Guarded: in some Android WebViews DOM storage is disabled and
    // window.localStorage is null, so accessing it throws. Fall through to
    // the browser-language / merchant-default branches instead of crashing.
    try {
      const storedLang = localStorage.getItem('preferredLanguage');
      if (storedLang && supportedLanguages.includes(storedLang)) {
        return storedLang;
      }
    } catch {
      /* storage unavailable (WebView with DOM storage disabled / private mode) */
    }

    // 3. Check browser language (user's browser setting)
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang) {
      // Extract language code (e.g., 'de' from 'de-DE')
      const langCode = browserLang.split('-')[0].toLowerCase();
      if (supportedLanguages.includes(langCode)) {
        return langCode;
      }
    }

    // 4. Use merchant default or fallback to 'en'
    return merchantData?.default_language || 'en';
  };

  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage());

  // Sync i18next language with current language, loading translations on demand.
  //
  // loadLanguage() does a dynamic import() of the locale chunk, which can reject
  // when a stale (atomic-deploy-invalidated) chunk hash 404s or a flaky mobile
  // connection drops the fetch — Safari surfaces this as "Importing a module
  // script failed". A missing locale chunk is non-fatal (i18next keeps the
  // already-loaded/English fallback), so we deliberately fall back here rather
  // than force a reload: a reload mid-checkout would wipe the contact form. The
  // .catch() keeps this off the global onunhandledrejection handler; we log at
  // warning level so the stale-deploy rate stays visible without being noise.
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      loadLanguage(currentLanguage)
        .then(() => {
          i18n.changeLanguage(currentLanguage);
        })
        .catch((err) => {
          debug.warn(`Failed to load translations for '${currentLanguage}', keeping fallback`, err);
          captureWarning('Locale chunk load failed — keeping fallback language', {
            language: currentLanguage,
            errorName: err?.name,
            errorMessage: typeof err?.message === 'string' ? err.message.slice(0, 200) : undefined,
          }, { flow: 'i18n', stage: 'locale-load' });
        });
    }
  }, [currentLanguage, i18n]);

  // Sync HTML lang attribute for WCAG 3.1.1 (Language of Page)
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Update URL parameter when language changes (HashRouter compatible).
  // Guard with a ref so we only call replaceState when the language truly
  // changes, protecting against iOS browsers where replaceState with a
  // modified hash may fire a spurious hashchange → re-render loop.
  const lastSyncedLang = React.useRef(null);
  useEffect(() => {
    if (lastSyncedLang.current === currentLanguage) return;
    lastSyncedLang.current = currentLanguage;

    // For HashRouter, query params are part of the hash fragment
    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');

    // Split hash into path and query parts
    const hashPath = queryStart !== -1 ? hash.substring(0, queryStart) : hash;
    const queryString = queryStart !== -1 ? hash.substring(queryStart + 1) : '';

    const urlParams = new URLSearchParams(queryString);
    const urlLang = urlParams.get('lang');

    if (urlLang !== currentLanguage) {
      // Update language parameter
      urlParams.set('lang', currentLanguage);

      // Reconstruct hash with updated query params
      const newHash = `${hashPath}?${urlParams.toString()}`;
      const newUrl = `${window.location.pathname}${window.location.search}${newHash}`;

      window.history.replaceState({}, '', newUrl);
    }
  }, [currentLanguage]);

  /**
   * Change current language
   * @param {string} lang - Language code (e.g., 'en', 'de')
   */
  const changeLanguage = (lang) => {
    const supportedLanguages = merchantData?.supported_languages || ['en'];
    if (supportedLanguages.includes(lang)) {
      setCurrentLanguage(lang);
      // Store in localStorage for persistence (best-effort: storage may be
      // unavailable in some WebViews / private mode, where access throws).
      try {
        localStorage.setItem('preferredLanguage', lang);
      } catch {
        /* storage unavailable — language still applies for this session */
      }
    } else {
      debug.warn(`Language '${lang}' is not supported by this merchant`);
    }
  };

  /**
   * Get merchant-specific translation from merchant_translations table data
   *
   * Fallback logic (NO REDUNDANCY):
   * 1. If current language == default language → use mainTableValue (default language in main table)
   * 2. Else try translation table for current language
   * 3. If not found → use mainTableValue (fallback to default language)
   *
   * @param {string} mainTableValue - Value from main table (in default language)
   * @param {array} merchantTranslations - Array of translation objects from database (non-default languages only)
   * @param {string} field - Field name to translate (e.g., 'business_name', 'description')
   * @returns {string} Translated text
   */
  const getMerchantTranslation = (mainTableValue, merchantTranslations, field) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table (no need to check translations)
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (merchantTranslations && Array.isArray(merchantTranslations)) {
      const translation = merchantTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation[field]) {
        return translation[field];
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  /**
   * Get product type translation from product_type_translations table data
   *
   * @param {string} mainTableValue - Value from main table (in default language)
   * @param {array} productTranslations - Array of translation objects from database (non-default languages only)
   * @param {string} field - Field name to translate
   * @returns {string} Translated text
   */
  const getProductTranslation = (mainTableValue, productTranslations, field) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (productTranslations && Array.isArray(productTranslations)) {
      const translation = productTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation[field]) {
        return translation[field];
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  /**
   * Get pricing rule translation
   *
   * @param {string} mainTableValue - Duration label from main table (in default language)
   * @param {array} pricingTranslations - Array of translation objects from database (non-default languages only)
   * @returns {string} Translated duration label
   */
  const getPricingTranslation = (mainTableValue, pricingTranslations) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (pricingTranslations && Array.isArray(pricingTranslations)) {
      const translation = pricingTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation.duration_label) {
        return translation.duration_label;
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  /**
   * Get location translation from location_translations table data
   *
   * @param {string} mainTableValue - Value from main table (in default language)
   * @param {array} locationTranslations - Array of translation objects from database (non-default languages only)
   * @param {string} field - Field name to translate (e.g., 'location_name')
   * @returns {string} Translated text
   */
  const getLocationTranslation = (mainTableValue, locationTranslations, field) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (locationTranslations && Array.isArray(locationTranslations)) {
      const translation = locationTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation[field]) {
        return translation[field];
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  /**
   * Get inventory item translation from inventory_item_translations table data
   *
   * @param {string} mainTableValue - Value from main table (in default language)
   * @param {array} inventoryTranslations - Array of translation objects from database (non-default languages only)
   * @param {string} field - Field name to translate (e.g., 'inventory_specific_info')
   * @returns {string} Translated text
   */
  const getInventoryTranslation = (mainTableValue, inventoryTranslations, field) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (inventoryTranslations && Array.isArray(inventoryTranslations)) {
      const translation = inventoryTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation[field]) {
        return translation[field];
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  const value = {
    // Current language state
    currentLanguage,
    changeLanguage,

    // Merchant configuration
    supportedLanguages: merchantData?.supported_languages || ['en'],
    defaultLanguage: merchantData?.default_language || 'en',

    // Translation utilities for dynamic content (no redundancy)
    // All functions now require mainTableValue + translations array
    getMerchantTranslation,
    getProductTranslation,
    getPricingTranslation,
    getLocationTranslation,
    getInventoryTranslation,

    // Raw merchant data for advanced use
    merchantData,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to access language context
 *
 * @returns {object} Language context with translation functions
 * @throws {Error} If used outside LanguageProvider
 *
 * @example
 * const { currentLanguage, getProductTranslation, getLocationTranslation, getInventoryTranslation } = useLanguage();
 *
 * // Product translation
 * // product.product_name contains default language (from main table)
 * // product.translations contains non-default languages (from translation table)
 * const productName = getProductTranslation(
 *   product.product_name,          // Default language from main table
 *   product.translations,           // Non-default languages from translation table
 *   'product_name'                  // Field name
 * );
 *
 * // Location translation
 * const locationName = getLocationTranslation(
 *   location.location_name,         // Default language from main table
 *   location.translations,          // Non-default languages from translation table
 *   'location_name'                 // Field name
 * );
 *
 * // Inventory item translation
 * const inventoryInfo = getInventoryTranslation(
 *   item.inventory_specific_info,   // Default language from main table
 *   item.translations,              // Non-default languages from translation table
 *   'inventory_specific_info'       // Field name
 * );
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};

/**
 * HOC to provide language context to a component
 */
export const withLanguage = (Component) => {
  return (props) => {
    const language = useLanguage();
    return <Component {...props} language={language} />;
  };
};

export default LanguageContext;
